"use client";

import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { RboPropData } from "commonModule/services/common";
import CancellationPolicyIcon from "components/icons-svg/CancellationPolicyIcon";
import DocumentIcon from "components/icons-svg/DocumentIcon";
import HomeIconForHouseRule from "components/icons-svg/HomeIconForHouseRule";
import SecurityDepositIcon from "components/icons-svg/SecurityDepositIcon";
import usePropertyAdditionalInfo from "hooks/swr/usePropertyAdditionalInfo";
import { selectBookingStore } from "stores/booking/bookingSlice";
import { useAppSelector } from "stores/hooks";
import { useDisclosure } from "utils/hooks/useDisclosure";
import { calculateFormattedBeforeDate } from "utils/property-utils";
import { HouseRules } from "./house-rules/HouseRules";
const CancellationPolicyModal = dynamic(() => import( /* webpackChunkName: "CancellationPolicyModal" */"./CancellationPolicyModal"), {
  ssr: false
});
const SecurityDepositModal = dynamic(() => import( /* webpackChunkName: "SecurityDepositModal" */"./safety-and-property/SecurityDepositModal"), {
  ssr: false
});
export const Item = ({
  name
}: {
  name: string;
}) => {
  return <div className='inline-block w-full' data-sentry-component="Item" data-sentry-source-file="ThingsToKnow.tsx">
      <div className='mb-1 flex w-full items-center gap-2 md:mb-2'>
        <div className='flex h-[5px] min-h-[5px] w-[5px] min-w-[5px] items-center justify-center rounded-full bg-[#8D91A5] text-white' />
        <p className='text-sm font-medium leading-[23.8px] text-Gray-700'>
          {name}
        </p>
      </div>
    </div>;
};
const ThingsToKnow = ({
  checkInTime,
  checkoutTime,
  partyAllowed,
  petAllowed,
  smokeAllowed,
  minimum_age,
  rboPropData,
  propertyId,
  securityDepositText,
  cancelPolicyDatelessText,
  cancelPolicyFutureRefundText,
  cancelPolicyPastRefundText
}: {
  checkInTime: string;
  checkoutTime: string;
  partyAllowed: boolean;
  petAllowed: boolean;
  smokeAllowed: boolean;
  minimum_age?: number | null;
  rboPropData?: RboPropData | null;
  propertyId?: string;
  securityDepositText: string | undefined;
  cancelPolicyDatelessText?: string | null;
  cancelPolicyFutureRefundText?: string | null;
  cancelPolicyPastRefundText?: string | null;
}) => {
  const r = useTranslations("rules");
  const {
    isOpen: isCancellationPolicyModalOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const {
    isOpen: isSecurityDepositModalOpen,
    onOpen: onSecurityDepositOpen,
    onClose: onSecurityDepositClose
  } = useDisclosure();
  const bookingStore = useAppSelector(selectBookingStore);
  const {
    data
  } = usePropertyAdditionalInfo(propertyId || "");
  const cancellationPolicy = data?.cancellationPolicy;
  const validToDays = cancellationPolicy?.policy ? cancellationPolicy.policy[0].validTo : 0;
  const {
    formattedBeforeDate,
    isPastDate
  } = calculateFormattedBeforeDate(bookingStore?.startDate, validToDays);
  let newDescriptionText: string | null | undefined = null;
  if (!bookingStore?.startDate) {
    newDescriptionText = data?.cancelPolicyDatelessText || cancelPolicyDatelessText;
  } else if (!isPastDate) {
    newDescriptionText = data?.cancelPolicyFutureRefundText || cancelPolicyFutureRefundText;
  } else {
    newDescriptionText = data?.cancelPolicyPastRefundText || cancelPolicyPastRefundText;
  }
  if (newDescriptionText) {
    newDescriptionText = newDescriptionText.replace(/@@refundDate@@/g, formattedBeforeDate).replace(/@@checkInDate@@/g, formattedBeforeDate).replace(/@@daysBeforeCheckIn@@/g, formattedBeforeDate) // or actual check-in date
    .replace(/@@genericDays@@/g, validToDays ? `${validToDays.toString()} days` : "30 days");
  }

  // removing the leading zero and converting AM/PM to lowercase
  const formatTime = (timeStr: string) => {
    return timeStr.replace(/^0/, "").replace(" ", "")?.toLowerCase();
  };
  return <>
      <div className='mb-5 flex items-center gap-2 bg-white md:mb-6'>
        <DocumentIcon className='h-[16px] w-[14px] md:h-[20px] md:w-[17.5px]' data-sentry-element="DocumentIcon" data-sentry-source-file="ThingsToKnow.tsx" />
        <h3 className='text-base font-semibold leading-[19.2px] tracking-[-0.24px] text-secondary500 md:text-xl md:leading-[30px] md:tracking-[-0.3px]'>
          Know before you go
        </h3>
      </div>
      <div className='lg4:flex'>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-x-8'>
          {rboPropData?.addHouse_rule ? <HouseRules houseRules={rboPropData?.addHouse_rule} /> : <div className='bg-white'>
              <div className='mb-1 flex items-center gap-2 md:mb-2'>
                <HomeIconForHouseRule />
                <p className='text-sm font-semibold leading-7 text-brand-dark md:text-base'>
                  House rules
                </p>
              </div>
              <div className='block columns-2 pl-3.5 md:columns-1 md:pl-[7px]'>
                <Item name={`${r("checkin")}: ${formatTime(checkInTime)}`} />
                {/* TODO: Update language JSON for Checkout */}
                <Item name={`Checkout: ${formatTime(checkoutTime)}`} />
                <Item name={petAllowed ? r("petsallowed") : r("nopets") + " allowed"} />
                {minimum_age && minimum_age > 0 && <Item name={`Minimum Age to Rent: ${minimum_age}`} />}
                <Item name={smokeAllowed ? r("smokingisallowed") : r("nonsmoking")} />
                <Item name={partyAllowed ? r("partyallowed") : r("noparties")} />
              </div>
            </div>}

          <div>
            {securityDepositText && <div className='mb-6 bg-white md:mb-[32px]'>
                <div className='mb-1 flex items-center gap-2 md:mb-2'>
                  <SecurityDepositIcon className='h-4 w-4 md:h-5 md:w-5' />
                  <p className='text-sm font-semibold leading-7 text-brand-dark md:text-base'>
                    Security deposit
                  </p>
                </div>
                <p className='pl-6 text-sm font-medium leading-6 text-Gray-700 md:pl-0'>
                  {securityDepositText?.split("||")?.[0]}
                </p>
                <button className='mt-2 flex h-auto justify-start bg-white pl-6 text-left text-sm font-semibold text-accent-500 underline md:mt-4 md:pl-0' onClick={onSecurityDepositOpen}>
                  See details
                </button>
              </div>}

            {cancellationPolicy?.policy && cancellationPolicy?.policy[0]?.validTo && <div className='bg-white'>
                  <div className='mb-1 flex items-center gap-2 md:mb-2'>
                    <CancellationPolicyIcon />
                    <p className='text-sm font-semibold leading-7 text-brand-dark md:text-base'>
                      Host’s cancellation policy
                    </p>
                  </div>

                  <div className='flex flex-col gap-2 pl-6 md:pl-0'>
                    <p className='text-sm font-medium leading-6 text-Gray-700 sm:pr-4'>
                      {newDescriptionText ? newDescriptionText?.split("||")?.[0] : cancellationPolicy?.policy[0]?.description?.split("||")?.[0]}
                    </p>
                  </div>
                  <button className='mt-2 flex h-auto justify-start bg-white pl-6 text-left text-sm font-semibold text-accent-500 underline md:mt-4 md:pl-0' onClick={onOpen} data-track-v2-event='PDP_CANCELLATION_POLICY_CLICK' data-track-v2-props={JSON.stringify({
              propertyId: propertyId || ""
            })}>
                    See details
                  </button>
                </div>}
          </div>
        </div>
      </div>
      {isCancellationPolicyModalOpen && cancellationPolicy && <CancellationPolicyModal isOpen={isCancellationPolicyModalOpen} onClose={onClose} cancellationPolicy={cancellationPolicy} checkInDate={bookingStore?.startDate} isDetailPage={true} cancelPolicyDatelessText={data?.cancelPolicyDatelessText || cancelPolicyDatelessText} cancelPolicyFutureRefundText={data?.cancelPolicyFutureRefundText || cancelPolicyFutureRefundText} cancelPolicyPastRefundText={data?.cancelPolicyPastRefundText || cancelPolicyPastRefundText} />}
      {isSecurityDepositModalOpen && securityDepositText && <SecurityDepositModal isOpen={isSecurityDepositModalOpen} onClose={onSecurityDepositClose} securityDepositModalText={securityDepositText?.split("||")?.[1]} />}
    </>;
};
export default ThingsToKnow;